import { useCallback, useEffect, useRef } from 'react';
import { collection, query, where } from 'firebase/firestore';
import { auth, firestore, FIRESTORE_COLLECTIONS } from '../firebase';
import { useSharedQueryDocs } from '../utils/sharedFirestoreSubscription';
import { useMutedChatIds } from './useProfile';
import { useI18n } from '../i18n';
import { showInAppNotification } from '../utils/inAppNotification';
import { getUserProfile } from '../utils/userProfiles';

// Background/terminated push notifications are handled separately — see
// usePushNotifications.ts (token registration), useNotificationListeners.ts
// (tap-to-navigate), and functions/index.js (sendEventChatMessagePush /
// sendPlanChatMessagePush / sendPrivateChatMessagePush / sendMatchMessagePush).
// This hook covers only the foreground in-app banner, driven by the same
// Firestore listeners the chat list already uses (see useSharedQueryDocs
// below) — it works independently of FCM and needs no additional packages.

const getTimestampMs = (value: unknown): number => {
  if (!value) return 0;
  if (typeof value === 'string') {
    const ms = new Date(value).getTime();
    return Number.isNaN(ms) ? 0 : ms;
  }
  const v = value as any;
  if (typeof v.toDate === 'function') return v.toDate().getTime();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  return 0;
};

const firstString = (...values: unknown[]): string => {
  for (const v of values) {
    if (typeof v === 'string' && v.trim().length) return v.trim();
  }
  return '';
};

const isDeletedForUser = (data: any, uid: string): boolean => data?.deletedFor?.[uid] === true;

// Module-level variable so ChatDetailScreen can mark itself as "active"
// without needing prop drilling or context.
let _activeChatId: string | null = null;

export function setActiveChatId(chatId: string | null): void {
  _activeChatId = chatId;
}

export function useChatNotifications(uid: string | null): void {
  const { t } = useI18n();
  const prevTimestampsRef = useRef<Record<string, number>>({});
  const mutedChatIds = useMutedChatIds(uid);
  const mutedChatIdsRef = useRef(mutedChatIds);

  // Keep ref current without re-triggering subscriptions on every mute change
  useEffect(() => {
    mutedChatIdsRef.current = mutedChatIds;
  });

  const handleChatDocs = useCallback((docs: any[]) => {
    const currentUid = auth.currentUser?.uid;
    if (!currentUid) return;

    const prev = prevTimestampsRef.current;
    const updates: Record<string, number> = {};

    docs.forEach(d => {
      const data = d.data();
      const chatId: string = d.id;
      const ts = getTimestampMs(data.lastMessageAt);
      const senderId = firstString(data.lastMessageSenderId);

      if (
        chatId in prev &&
        ts > (prev[chatId] ?? 0) &&
        senderId &&
        senderId !== currentUid &&
        !mutedChatIdsRef.current.has(chatId) &&
        chatId !== _activeChatId &&
        !isDeletedForUser(data, currentUid)
      ) {
        const senderName = firstString(data.lastMessageSenderName, data.senderName) || t('chat.traveler');
        const description = firstString(data.lastMessage) || t('chat.newMessageFallback');
        void getUserProfile(senderId).then(profile => {
          showInAppNotification({
            title: t('chat.newMessage'),
            description,
            sender: {
              name: profile.displayName || profile.name || senderName,
              photoUrl: profile.profileImage || profile.profilePhotoURL,
            },
          });
        });
      }

      updates[chatId] = ts;
    });

    prevTimestampsRef.current = { ...prev, ...updates };
  }, [t]);

  useEffect(() => {
    if (!uid) {
      prevTimestampsRef.current = {};
    }
  }, [uid]);

  // Shared keys: identical queries are opened by useChats/useChatUnreadSummary
  // in useProfile.ts (chat list screen + nav-root unread badge), so this
  // reuses those listeners instead of opening 3 more of its own.
  const { docs: eventChatDocs } = useSharedQueryDocs(
    uid ? `eventChats:${uid}` : null,
    () => query(collection(firestore, FIRESTORE_COLLECTIONS.eventChats), where('memberIds', 'array-contains', uid as string)),
  );
  const { docs: planChatDocs } = useSharedQueryDocs(
    uid ? `planChats:${uid}` : null,
    () => query(collection(firestore, FIRESTORE_COLLECTIONS.planChats), where('memberIds', 'array-contains', uid as string)),
  );
  const { docs: matchDocs } = useSharedQueryDocs(
    uid ? `matches:${uid}` : null,
    () => query(collection(firestore, FIRESTORE_COLLECTIONS.matches), where('users', 'array-contains', uid as string)),
  );
  const { docs: privateChatDocs } = useSharedQueryDocs(
    uid ? `privateChats:${uid}` : null,
    () => query(collection(firestore, FIRESTORE_COLLECTIONS.privateChats), where('users', 'array-contains', uid as string)),
  );

  useEffect(() => {
    if (!uid) return;
    handleChatDocs(eventChatDocs);
  }, [uid, eventChatDocs, handleChatDocs]);

  useEffect(() => {
    if (!uid) return;
    handleChatDocs(planChatDocs);
  }, [uid, planChatDocs, handleChatDocs]);

  useEffect(() => {
    if (!uid) return;
    handleChatDocs(matchDocs);
  }, [uid, matchDocs, handleChatDocs]);

  useEffect(() => {
    if (!uid) return;
    handleChatDocs(privateChatDocs);
  }, [uid, privateChatDocs, handleChatDocs]);
}
